import { Moment } from 'moment';
import { useState, useEffect, useMemo } from 'react';

import { TextField } from '@mui/material';
import {
  DateRangePicker,
  DateRange,
  SingleInputDateRangeField,
  PickersShortcutsItem,
} from '@mui/x-date-pickers-pro';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateFormat, formatDate, getDateInMoment } from '../../util/casMoment';

interface IDateRangeObj {
  start: string | null;
  end: string | null;
}

interface IShorcutObj {
  label: string;
  start: Moment;
  end: Moment;
}

interface Props {
  value: IDateRangeObj;
  onChange: (_: any, value: IDateRangeObj | null) => void;
  label?: string;
  shortcuts?: IShorcutObj[];
  className?: string;
}

export default function CasDateRangePicker({
  value: parmValue,
  onChange,
  label,
  shortcuts: parmShortcuts = [],
  ...other
}: Props) {
  const currentYear = getDateInMoment().year();

  const { start, end } = parmValue || {};

  const shortcuts: PickersShortcutsItem<DateRange<Moment>>[] = parmShortcuts.map((shortcut) => ({
    label: shortcut.label,
    getValue: () => [shortcut.start, shortcut.end],
  }));

  const value: DateRange<Moment> = useMemo(() => 
    [
      start ? getDateInMoment(start) : null, 
      end ? getDateInMoment(end) : null,
    ],
    [start, end]
  );

  const [tempValue, setTempValue] = useState<DateRange<Moment>>(value);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const handleChange = (date: DateRange<Moment>) => {
    setTempValue(date);
  };

  const handleAccept = (date: DateRange<Moment>) => {
    const [startDate, endDate] = date;

    if (!startDate && !endDate) {
      onChange(null, null);
      return;
    }
    const formattedStartDate = startDate ? formatDate(startDate, DateFormat.YMD) : null;
    const formattedEndDate = endDate ? formatDate(endDate, DateFormat.YMD) : null;

    onChange(null, {
      start: formattedStartDate,
      end: formattedEndDate,
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DateRangePicker
        value={tempValue}
        onChange={handleChange}
        onAccept={handleAccept}
        showDaysOutsideCurrentMonth
        format="MM/DD/YY"
        slots={{
          field: SingleInputDateRangeField,
          textField: TextField,
        }}
        slotProps={{
          textField: {
            size: 'small',
            label,
          },
          toolbar: {
            hidden: false,
            toolbarFormat: tempValue?.[0]?.year() !== currentYear ? 'MMM DD, YYYY' : 'MMM DD',
          },
          shortcuts: {
            items: shortcuts,
          },
          actionBar: {
            actions: ['clear', 'today', 'cancel', 'accept'],
          },
        }}
        {...other}
      />
    </LocalizationProvider>
  );
}
